import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { placementTargets } from "../api/queries";
import { ApiError } from "../api/client";
import type { PlacementTargetView } from "../api/types";
import { useCharacter } from "../character";
import { ErrorNote } from "./ErrorNote";
import { Sheet } from "./Sheet";
import { Stepper } from "./Stepper";
import { Value } from "./Value";

export type PlacementRole = "body" | "storage" | "place" | "carrier" | "container" | "item";

/** The target „Mitnehmen“: a carrier becomes a location of its own again, anything else goes onto the body; the server names it, too. */
export const takeAlong = "@root";

/** A node of the location tree as a module's view lists it; loads are value keys the module's engine computes. */
export interface PlacementNode {
  id: string;
  parent: string | null;
  role: PlacementRole;
  label: string;
  /** How many pieces the line holds; moving a single line with more than one asks how many go. */
  quantity?: number | null;
  /** Value key of the load in or on the node. */
  loadKey?: string | null;
  /** Value key of what the node may carry. */
  capacityKey?: string | null;
  /** Value key of the load that counts against the capacity, when it is not the whole load. */
  carriedKey?: string | null;
}

/** What a row of the tree gets besides its node: the move action, the shortcut to storage or back to the body, and the selection. */
export interface PlacementRowContext {
  depth: number;
  /** The containers above the node below its root, outermost first. */
  path: string[];
  /** While several things are selected, a tap on the row selects; rows leave out their own actions then. */
  selecting: boolean;
  /** The node lies under the body, also inside a container there. */
  carried: boolean;
  /** Button „<label> verschieben“ that opens „Verschieben nach …“. */
  moveButton: ReactNode;
  /** One tap to storage, or back to the body from anywhere else; null when the tree has no such root. */
  shortcut: ReactNode;
}

// Body, carriers, storage, then the other places (O5); places among themselves keep the order of the module.
const rootRank: Record<PlacementRole, number> = { body: 0, carrier: 1, storage: 2, place: 3, container: 4, item: 4 };
const byLabel = new Intl.Collator("de").compare;
const longPressMs = 500;
const searchFrom = 15;

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Only a convenience; without storage the page works the same.
  }
}

function useFinePointer() {
  const query = "(pointer: fine)";
  const [fine, setFine] = useState(() => typeof window.matchMedia === "function" && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(query);
    const change = () => setFine(media.matches);
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);
  return fine;
}

function useTreeIndex(nodes: PlacementNode[]) {
  return useMemo(() => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const isRoot = (n: PlacementNode) => n.parent === null || !byId.has(n.parent);
    const children = new Map<string, PlacementNode[]>();
    for (const node of nodes) {
      if (isRoot(node)) continue;
      const list = children.get(node.parent!) ?? [];
      list.push(node);
      children.set(node.parent!, list);
    }
    for (const list of children.values()) list.sort((a, b) => byLabel(a.label, b.label));
    const roots = nodes.filter(isRoot).sort((a, b) => rootRank[a.role] - rootRank[b.role] || (a.role === "place" ? 0 : byLabel(a.label, b.label)));
    const rootOf = (id: string): PlacementNode | undefined => {
      let node = byId.get(id);
      while (node && !isRoot(node)) node = byId.get(node.parent!);
      return node;
    };
    const subtree = (id: string): PlacementNode[] => (children.get(id) ?? []).flatMap((c) => [c, ...subtree(c.id)]);
    return {
      byId,
      roots,
      isRoot,
      children: (id: string) => children.get(id) ?? [],
      rootOf,
      subtree,
      body: roots.find((r) => r.role === "body"),
      storage: roots.find((r) => r.role === "storage"),
    };
  }, [nodes]);
}

/** „Traglast 12 von 16“, „Last 30 von 40“ or „Last 3“: the values come from the engine, the page only shows them. */
function LoadFigure({ node }: { node: PlacementNode }) {
  if (node.capacityKey && (node.carriedKey ?? node.loadKey)) {
    return (
      <>
        {node.carriedKey ? "Traglast" : "Last"} <Value valueKey={node.carriedKey ?? node.loadKey!} /> von <Value valueKey={node.capacityKey} />
      </>
    );
  }
  return node.loadKey ? (
    <>
      Last <Value valueKey={node.loadKey} />
    </>
  ) : null;
}

function useLoadText() {
  const { value, display } = useCharacter();
  return (node: PlacementNode | undefined) => {
    if (!node) return null;
    const shown = (key: string) => display(value(key)) || "–";
    if (node.capacityKey && (node.carriedKey ?? node.loadKey)) return `${node.carriedKey ? "Traglast" : "Last"} ${shown(node.carriedKey ?? node.loadKey!)} von ${shown(node.capacityKey)}`;
    return node.loadKey ? `Last ${shown(node.loadKey)}` : null;
  };
}

/**
 * Things grouped by where they lie: a section per root (body, carriers, storage, places) with its load, containers that open and close
 * with their contents indented, carriers standing in storage or at a place below it. Moving goes through one sheet for every device (row
 * action, a selection of several things, the last target as shortcut); a single line of several pieces asks how many go. With a fine
 * pointer rows can also be dragged onto a root or a container. Every way ends in onMove.
 */
export function PlacementTree(props: {
  nodes: PlacementNode[];
  /** Key in the browser for open groups and the last target, e.g. per character. */
  storageKey: string;
  renderRow: (node: PlacementNode, context: PlacementRowContext) => ReactNode;
  /** Buttons in the heading of a root, e.g. to edit a carrier. */
  rootActions?: (node: PlacementNode) => ReactNode;
  /** Content under the heading of a root, shown also while it is closed, e.g. a hint about the place. */
  rootDetails?: (node: PlacementNode) => ReactNode;
  /** Shows only the things that match, with the containers above them; every group is open while it is set. Rows are sorted by label. */
  filter?: ((node: PlacementNode) => boolean) | null;
  /** Moves the things into the target, of a single line only the quantity when it is given; true when it was applied. */
  onMove: (ids: string[], target: string, quantity?: number) => Promise<boolean>;
  /** Further controls in the line above the tree, e.g. filters. */
  toolbar?: ReactNode;
}) {
  const { character } = useCharacter();
  const index = useTreeIndex(props.nodes);
  const fine = useFinePointer();
  const openKey = `${props.storageKey}.open`;
  const lastKey = `${props.storageKey}.last`;
  const [opened, setOpened] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(readStored(openKey) ?? "{}") as Record<string, boolean>;
    } catch {
      return {};
    }
  });
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [moving, setMoving] = useState<string[] | null>(null);
  const [lastTarget, setLastTarget] = useState(() => readStored(lastKey));
  const [drag, setDrag] = useState<{ ids: string[]; targets: Map<string, PlacementTargetView> | null } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const press = useRef<{ timer: number; x: number; y: number; fired: boolean } | null>(null);
  const filter = props.filter ?? null;

  // Body and containers start open, storage, places and carriers on their own closed; a carrier standing somewhere opens like a container.
  const openByDefault = (node: PlacementNode) => node.role === "body" || node.role === "container" || (node.role === "carrier" && !index.isRoot(node));
  const isOpen = (node: PlacementNode) => filter !== null || (opened[node.id] ?? openByDefault(node));
  const toggleOpen = (node: PlacementNode) => {
    const next = { ...opened, [node.id]: !isOpen(node) };
    setOpened(next);
    writeStored(openKey, JSON.stringify(next));
  };
  const shown = (node: PlacementNode): boolean => filter === null || filter(node) || index.children(node.id).some(shown);

  const endSelection = useCallback(() => setSelected(null), []);
  useEffect(() => {
    if (!selected || moving) return;
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && endSelection();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected, moving, endSelection]);
  // Things that vanished, e.g. after an undo, leave the selection.
  useEffect(() => {
    setSelected((s) => (s && [...s].some((id) => !index.byId.has(id)) ? new Set([...s].filter((id) => index.byId.has(id))) : s));
  }, [index]);

  const toggleSelected = (id: string) =>
    setSelected((s) => {
      const next = new Set(s ?? []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const moveTo = async (ids: string[], target: string, remember: boolean, quantity?: number) => {
    if (remember) {
      setLastTarget(target);
      writeStored(lastKey, target);
    }
    const done = await props.onMove(ids, target, quantity);
    if (done) {
      setSelected(null);
      // Focus goes back to the moved row, or to the heading of a carrier taken along, which is a group of its own now; in a closed group
      // to the target, or the heading of its group.
      const root = index.rootOf(target)?.id ?? target;
      window.setTimeout(() => {
        const into = (selector: string) => document.querySelector<HTMLElement>(selector);
        (
          into(`[data-node="${CSS.escape(ids[0]!)}"] .placement-row-main button`) ??
          into(`[data-root="${CSS.escape(ids[0]!)}"] .placement-toggle`) ??
          into(`[data-node="${CSS.escape(target)}"] .placement-row-main button`) ??
          into(`[data-root="${CSS.escape(root)}"] .placement-toggle`) ??
          into(`[data-root="${CSS.escape(index.body?.id ?? "")}"] .placement-toggle`)
        )?.focus();
      }, 0);
    }
    return done;
  };

  const startDrag = (node: PlacementNode, e: DragEvent<HTMLElement>) => {
    const ids = selected?.has(node.id) ? [...selected] : [node.id];
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", ids.join(","));
    const row = e.currentTarget.closest(".placement-row");
    if (row) e.dataTransfer.setDragImage(row, 16, 16);
    setDrag({ ids, targets: null });
    placementTargets(character.id, ids).then(
      (list) => setDrag((d) => (d && d.ids === ids ? { ...d, targets: new Map(list.map((t) => [t.id, t])) } : d)),
      () => undefined,
    );
  };
  const endDrag = () => {
    setDrag(null);
    setOver(null);
  };
  /** A root or container as drop target; the server's answer on the targets says which are blocked and why. */
  const dropTarget = (id: string) => {
    if (!fine || !drag) return {};
    const target = drag.targets?.get(id);
    const blocked = target ? !target.allowed || target.current : false;
    return {
      title: blocked ? (target?.reason ?? "jetzt hier") : undefined,
      "data-drop": over === id ? (blocked ? "blocked" : "ok") : undefined,
      onDragOver: (e: DragEvent<HTMLElement>) => {
        e.stopPropagation();
        if (over !== id) setOver(id);
        if (blocked) {
          e.dataTransfer.dropEffect = "none";
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      },
      onDrop: (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const ids = drag.ids;
        endDrag();
        if (!blocked) void moveTo(ids, id, true);
      },
    };
  };

  const clearPress = () => {
    if (press.current) window.clearTimeout(press.current.timer);
  };
  const pressHandlers = (node: PlacementNode) => ({
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      // A new touch ends what an earlier long press left: a click after it may never come, e.g. on iOS.
      clearPress();
      press.current = null;
      // A long press starts the selection on touch; with a mouse there is the button and dragging.
      if (selected || e.pointerType === "mouse" || e.button !== 0) return;
      const state = { timer: 0, x: e.clientX, y: e.clientY, fired: false };
      state.timer = window.setTimeout(() => {
        state.fired = true;
        setSelected(new Set([node.id]));
      }, longPressMs);
      press.current = state;
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      const p = press.current;
      if (p && !p.fired && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 10) {
        clearPress();
        press.current = null;
      }
    },
    onPointerUp: clearPress,
    onPointerCancel: () => {
      clearPress();
      press.current = null;
    },
    onContextMenu: (e: MouseEvent) => {
      if (press.current) e.preventDefault();
    },
    onClickCapture: (e: MouseEvent) => {
      if (press.current?.fired) {
        press.current = null;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      press.current = null;
      if (selected && !(e.target as HTMLElement).closest(".placement-check, .disclosure-button")) {
        e.preventDefault();
        e.stopPropagation();
        toggleSelected(node.id);
      }
    },
  });

  const moveButton = (node: PlacementNode) => (
    <button type="button" className="icon-button" aria-label={`${node.label} verschieben`} title="Verschieben nach …" onClick={() => setMoving([node.id])}>
      ⇄
    </button>
  );

  const contextOf = (node: PlacementNode, depth: number, path: string[]): PlacementRowContext => {
    const root = index.rootOf(node.id);
    const carried = root?.role === "body";
    // Taking along puts a thing onto the body and makes a carrier standing somewhere a location of its own again.
    const target = carried ? index.storage?.id : (index.body || node.role === "carrier") ? takeAlong : undefined;
    return {
      depth,
      path,
      selecting: selected !== null,
      carried,
      moveButton: moveButton(node),
      shortcut: target ? (
        <button
          type="button"
          className="button secondary small"
          aria-label={`${node.label} ${carried ? "ins Lager" : "mitnehmen"}`}
          onClick={() => void moveTo([node.id], target, false)}
        >
          {carried ? "Ins Lager" : "Mitnehmen"}
        </button>
      ) : null,
    };
  };

  const renderItems = (parent: string, depth: number, path: string[]): ReactNode =>
    index
      .children(parent)
      .filter(shown)
      .map((node) => {
        const kids = index.children(node.id).filter(shown);
        const open = isOpen(node);
        const holds = node.role === "container" || node.role === "carrier";
        return (
          <li key={node.id} className="placement-item" data-node={node.id} data-label={node.label}>
            <div
              className={`placement-row${selected?.has(node.id) ? " selected" : ""}`}
              style={{ paddingLeft: `${Math.min(depth, 3) * 1.1}rem` }}
              {...pressHandlers(node)}
              {...(holds ? dropTarget(node.id) : {})}
            >
              {selected ? (
                <input type="checkbox" className="placement-check" checked={selected.has(node.id)} aria-label={`${node.label} auswählen`} onChange={() => toggleSelected(node.id)} />
              ) : (
                fine && (
                  <span className="drag-handle" draggable aria-hidden="true" title="Ziehen zum Verschieben" onDragStart={(e) => startDrag(node, e)} onDragEnd={endDrag}>
                    ⠿
                  </span>
                )
              )}
              {kids.length > 0 && (
                <button
                  type="button"
                  className="disclosure-button"
                  aria-expanded={open}
                  aria-label={`Inhalt von ${node.label} ${open ? "zuklappen" : "aufklappen"}`}
                  onClick={() => toggleOpen(node)}
                >
                  {open ? "▾" : "▸"}
                </button>
              )}
              <div className="placement-row-main">{props.renderRow(node, contextOf(node, depth, path))}</div>
            </div>
            {kids.length > 0 && open && <ul className="placement-children">{renderItems(node.id, depth + 1, [...path, node.label])}</ul>}
          </li>
        );
      });

  const movingLabel = moving && (moving.length === 1 ? (index.byId.get(moving[0]!)?.label ?? "") : `${moving.length} Gegenstände`);
  // A single line of several pieces asks how many go; several lines always move whole.
  const movingQuantity = moving?.length === 1 ? (index.byId.get(moving[0]!)?.quantity ?? 1) : 1;
  const selectedIds = selected ? [...selected] : [];

  return (
    <div className="placement-tree">
      <div className="placement-toolbar">
        {props.toolbar}
        <button type="button" className="button secondary small" aria-pressed={selected !== null} onClick={() => setSelected(selected ? null : new Set())}>
          {selected ? "Auswahl beenden" : "Auswählen"}
        </button>
      </div>
      {filter && !index.roots.some((root) => index.children(root.id).some(shown)) && <p className="list-empty">Keine Gegenstände gefunden.</p>}
      {index.roots.map((root) => {
        // With a filter, count and „Alle auswählen“ take only the matches, not the containers on the way to them.
        const items = index.subtree(root.id).filter((n) => filter === null || filter(n));
        const open = isOpen(root);
        if (filter && !index.children(root.id).some(shown)) return null;
        return (
          <section key={root.id} className="placement-root" aria-label={root.label} data-root={root.id} {...dropTarget(root.id)}>
            <div className="placement-root-head">
              <button type="button" className="placement-toggle" aria-expanded={open} onClick={() => toggleOpen(root)}>
                <span className="disclosure" aria-hidden="true">
                  {open ? "▾" : "▸"}
                </span>
                <span className="placement-root-label">{root.label}</span>
                <span className="muted small">{items.length === 1 ? "1 Gegenstand" : `${items.length} Gegenstände`}</span>
              </button>
              <span className="placement-load small">
                <LoadFigure node={root} />
              </span>
              {selected && items.length > 0 && (
                <button type="button" className="link small" onClick={() => setSelected(new Set([...selected, ...items.map((i) => i.id)]))}>
                  Alle auswählen
                </button>
              )}
              {props.rootActions?.(root)}
              {root.role === "carrier" && !selected && moveButton(root)}
            </div>
            {props.rootDetails?.(root)}
            {open && (index.children(root.id).length > 0 ? <ul className="placement-children top">{renderItems(root.id, 0, [])}</ul> : <p className="list-empty">Leer.</p>)}
          </section>
        );
      })}
      {selected && (
        <div className="selection-bar" role="toolbar" aria-label="Auswahl">
          <span className="selection-count">{selectedIds.length} ausgewählt</span>
          <button type="button" className="button primary small" disabled={selectedIds.length === 0} onClick={() => setMoving(selectedIds)}>
            Verschieben
          </button>
          {index.body && (
            <button type="button" className="button secondary small" disabled={selectedIds.length === 0} onClick={() => void moveTo(selectedIds, takeAlong, false)}>
              Mitnehmen
            </button>
          )}
          {index.storage && (
            <button type="button" className="button secondary small" disabled={selectedIds.length === 0} onClick={() => void moveTo(selectedIds, index.storage!.id, false)}>
              Ins Lager
            </button>
          )}
          <button type="button" className="button secondary small" onClick={endSelection}>
            Fertig
          </button>
        </div>
      )}
      {moving && (
        <MoveSheet
          ids={moving}
          what={movingLabel ?? ""}
          quantity={movingQuantity}
          nodes={props.nodes}
          lastTarget={lastTarget}
          onClose={() => setMoving(null)}
          onPick={(target, count) => {
            const ids = moving;
            setMoving(null);
            void moveTo(ids, target, true, count < movingQuantity ? count : undefined);
          }}
        />
      )}
    </div>
  );
}

/**
 * „Verschieben nach …“: every target as a tree with its load, the current place marked, blocked targets greyed out with the server's
 * reason, the last target as one tap. A line of several pieces asks how many go, all by default. Arrow keys walk the tree, Enter moves,
 * Escape closes.
 */
export function MoveSheet(props: {
  ids: string[];
  what: string;
  /** Pieces of the single line that moves; above 1 the sheet asks how many go. */
  quantity?: number;
  nodes: PlacementNode[];
  lastTarget: string | null;
  onPick: (target: string, quantity: number) => void;
  onClose: () => void;
}) {
  const { character } = useCharacter();
  const loadText = useLoadText();
  const byId = useMemo(() => new Map(props.nodes.map((n) => [n.id, n])), [props.nodes]);
  const [targets, setTargets] = useState<PlacementTargetView[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [focus, setFocus] = useState<string | null>(null);
  const total = props.quantity ?? 1;
  const [count, setCount] = useState(total);
  const pick = (target: string) => props.onPick(target, count);
  const list = useRef<HTMLUListElement>(null);
  const idsKey = props.ids.join(",");

  useEffect(() => {
    let cancelled = false;
    placementTargets(character.id, idsKey.split(",")).then(
      (result) => {
        if (cancelled) return;
        // „Mitnehmen“ first, then the roots in the order of the list: body, carriers, storage, places.
        const groups: PlacementTargetView[][] = [];
        for (const t of result) {
          if (t.depth === 0) groups.push([t]);
          else groups[groups.length - 1]?.push(t);
        }
        const rank = (t: PlacementTargetView) => (t.id === takeAlong ? -1 : rootRank[byId.get(t.id)?.role ?? "storage"]);
        const ordered = groups.sort((a, b) => rank(a[0]!) - rank(b[0]!)).flat();
        setTargets(ordered);
        const rootIds = groups.map((g) => g[0]!.id);
        setOpen(new Set(ordered.length <= searchFrom ? rootIds : groups.filter((g) => g.some((t) => t.current) || rank(g[0]!) === 0).map((g) => g[0]!.id)));
        setFocus(ordered.find((t) => t.current)?.id ?? ordered[0]?.id ?? null);
      },
      (e: unknown) => !cancelled && setError(e instanceof ApiError ? e : new ApiError(0, String(e))),
    );
    return () => {
      cancelled = true;
    };
  }, [character.id, idsKey, byId]);

  const parentOf = useMemo(() => new Map((targets ?? []).map((t) => [t.id, t.parent])), [targets]);
  const rootOf = (t: PlacementTargetView) => {
    let id: string = t.id;
    while (parentOf.get(id)) id = parentOf.get(id)!;
    return id;
  };
  const term = query.trim().toLocaleLowerCase("de");
  const matches = term ? new Set((targets ?? []).filter((t) => t.label.toLocaleLowerCase("de").includes(term)).map((t) => t.id)) : null;
  const visible = (targets ?? []).filter((t) => {
    if (matches) {
      // A match shows with the targets above it.
      return [...matches].some((m) => {
        let id: string | null | undefined = m;
        while (id) {
          if (id === t.id) return true;
          id = parentOf.get(id);
        }
        return false;
      });
    }
    return t.depth === 0 || open.has(rootOf(t));
  });
  const usable = (t: PlacementTargetView) => t.allowed && !t.current;
  const last = (targets ?? []).find((t) => t.id === props.lastTarget && usable(t));

  useEffect(() => {
    if (focus && list.current?.contains(document.activeElement)) list.current.querySelector<HTMLElement>(`[data-target="${CSS.escape(focus)}"]`)?.focus();
  }, [focus]);

  const toggle = (id: string, to?: boolean) =>
    setOpen((o) => {
      const next = new Set(o);
      if (to ?? !next.has(id)) next.add(id);
      else next.delete(id);
      return next;
    });
  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const at = visible.findIndex((t) => t.id === focus);
    const current = visible[at];
    const go = (i: number) => {
      const t = visible[Math.max(0, Math.min(visible.length - 1, i))];
      if (t) setFocus(t.id);
    };
    switch (e.key) {
      case "ArrowDown":
        go(at + 1);
        break;
      case "ArrowUp":
        go(at - 1);
        break;
      case "Home":
        go(0);
        break;
      case "End":
        go(visible.length - 1);
        break;
      case "ArrowRight":
        if (current?.depth === 0) toggle(current.id, true);
        break;
      case "ArrowLeft":
        if (current && current.depth === 0) toggle(current.id, false);
        else if (current) setFocus(rootOf(current));
        break;
      case "Enter":
      case " ":
        if (current && usable(current)) pick(current.id);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  return (
    <Sheet title="Verschieben nach …" onClose={props.onClose}>
      <div className="stack small-gap">
        <p className="muted small move-what">{total > 1 ? `${props.what} (${total} Stück)` : props.what}</p>
        {total > 1 && (
          <div className="move-quantity">
            <span aria-hidden="true">Anzahl</span>
            <Stepper label="Anzahl" typed suffix={`von ${total}`} value={count} min={1} max={total} onChange={(value) => setCount(value)} />
          </div>
        )}
        <ErrorNote error={error} />
        {last && (
          <button type="button" className="button secondary move-last" onClick={() => pick(last.id)}>
            Zuletzt: {last.label}
          </button>
        )}
        {targets && targets.length > searchFrom && (
          <label className="field">
            <span>Ziel suchen</span>
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
        )}
        {!targets && !error && <p className="muted small">Ziele werden geladen …</p>}
        {targets && (
          <ul ref={list} className="move-targets" role="tree" aria-label="Ziele" onKeyDown={onKeyDown}>
            {visible.map((t) => {
              const enabled = usable(t);
              const isRoot = t.depth === 0;
              const expanded = matches !== null || open.has(t.id);
              return (
                <li
                  key={t.id}
                  role="treeitem"
                  data-target={t.id}
                  aria-level={t.depth + 1}
                  aria-expanded={isRoot ? expanded : undefined}
                  aria-disabled={enabled ? undefined : true}
                  aria-current={t.current ? "location" : undefined}
                  aria-selected={false}
                  tabIndex={t.id === focus ? 0 : -1}
                  className={`move-target${enabled ? "" : " disabled"}${isRoot ? " root" : ""}`}
                  style={{ paddingLeft: `${0.5 + Math.min(t.depth, 4) * 1.1}rem` }}
                  onFocus={() => setFocus(t.id)}
                  onClick={() => enabled && pick(t.id)}
                >
                  {isRoot && (
                    <span
                      className="move-disclosure"
                      aria-hidden="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(t.id);
                      }}
                    >
                      {expanded ? "▾" : "▸"}
                    </span>
                  )}
                  <span className="move-target-main">
                    <span className="move-target-label">
                      {t.label}
                      {t.current && <span className="badge">jetzt hier</span>}
                    </span>
                    <span className="muted small">{t.allowed ? loadText(byId.get(t.id)) : t.reason}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
